import { gql, useQuery } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCurrency, formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";

const OfferDetailsQuery = gql`
  query GetOfferDetails($offerId: ID!) {
    offer(id: $offerId) {
      id
      validFrom
      value
      externalReference
      lineItems {
        id
        productName
        productExternalReference
        value
      }
    }
  }
`;

interface OfferLineItemNode {
  id: string;
  productName: string;
  productExternalReference: string;
  value: number | null;
}

interface OfferNode {
  id: string;
  validFrom: string | null;
  value: number | null;
  externalReference: string;
  lineItems: OfferLineItemNode[];
}

interface OfferDetailsData {
  offer: OfferNode | null;
}

export interface OfferLineItemDisplay {
  id: string;
  productName: string;
  productExternalReference: string;
  value: number | null;
  formattedValue: string;
}

export interface OfferDetails {
  id: string;
  externalReference: string;
  validFrom: string | null;
  formattedDate: string;
  value: number | null;
  formattedValue: string;
  lineItems: OfferLineItemDisplay[];
}

export function useOfferDetails(offerId: string | undefined): {
  isPending: boolean;
  error: Error | undefined;
  offer: OfferDetails | null;
} {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const [result] = useQuery<OfferDetailsData>({
    query: OfferDetailsQuery,
    variables: { offerId: offerId ?? "" },
    requestPolicy: "cache-first",
    pause: !offerId,
  });

  const { data, fetching, stale, error } = result;

  const offer =
    data?.offer == null
      ? null
      : {
          id: data.offer.id,
          externalReference: data.offer.externalReference,
          validFrom: data.offer.validFrom,
          formattedDate: data.offer.validFrom != null ? formatDayMonthYear(data.offer.validFrom, locale) : "—",
          value: data.offer.value ?? null,
          formattedValue: data.offer.value != null ? formatCurrency(data.offer.value, currency, locale) : "—",
          lineItems: (data.offer.lineItems || []).map((li) => ({
            id: li.id,
            productName: li.productName,
            productExternalReference: li.productExternalReference,
            value: li.value ?? null,
            formattedValue: li.value != null ? formatCurrency(li.value, currency, locale) : "—",
          })),
        };

  return {
    isPending: fetching || stale,
    error,
    offer,
  };
}
